import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  CreateHazardIdentificationPayload,
  HazardIdentification,
  UpdateHazardIdentificationPayload,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useMachineLocations } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { dialogPortaledPopoverGuard } from '@/components/ui/dialog-portaled-popover-guard'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useMachineryDirectiveEhsrList } from '../../hooks/useMachineryDirectiveEhsrList'
import { EhsrSelect } from './EhsrSelect'
import { PictogramSelect } from './PictogramSelect'
import {
  RiskInjurySelect,
  RiskFrequencySelect,
  RiskAvoidanceSelect,
  RiskLikelihoodSelect,
} from './RiskDimensionSelects'
import { HAZARD_LIFECYCLES, HAZARD_TYPES } from './constants/hazardEnums'
import {
  getMachineLocationDisplayName,
  sortMachineLocationsForList,
} from '../machine-locations/buildMachineLocationRows'

export type AddHazardDialogProps = {
  complianceId: string
  isOpen: boolean
  onClose: () => void
  onCreate: (payload: CreateHazardIdentificationPayload) => void
  onUpdate?: (
    payload: { hazardId: string } & UpdateHazardIdentificationPayload
  ) => void
  hazard?: HazardIdentification | null
  isCreating?: boolean
  isUpdating?: boolean
}

const initialForm = {
  riskInInjury: '',
  riskInFrequency: '',
  riskInAvoidance: '',
  riskInLikelihood: '',
  riskInInjuryJustification: '',
  riskInFrequencyJustification: '',
  riskInAvoidanceJustification: '',
  riskInLikelihoodJustification: '',
  machineLocationId: '',
  type: '',
  lifecycles: [] as string[],
  description: '',
  pictogramId: null as string | null,
  ehsrClauseIds: [] as string[],
}

function validateForm(form: typeof initialForm): boolean {
  return (
    form.machineLocationId.trim() !== '' &&
    form.type.trim() !== '' &&
    form.lifecycles.length >= 1 &&
    form.description.trim() !== '' &&
    form.riskInInjury.trim() !== '' &&
    form.riskInFrequency.trim() !== '' &&
    form.riskInAvoidance.trim() !== '' &&
    form.riskInLikelihood.trim() !== ''
  )
}

export function AddHazardDialog({
  complianceId,
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  hazard,
  isCreating = false,
  isUpdating = false,
}: AddHazardDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const ehsrOptions = useMachineryDirectiveEhsrList()
  const { data: machineLocations = [] } = useMachineLocations(complianceId)
  const sortedMachineLocations = useMemo(
    () => sortMachineLocationsForList(machineLocations),
    [machineLocations]
  )
  const machineLocationItems = useMemo(
    () =>
      sortedMachineLocations.map((location) => ({
        value: location.id,
        label: getMachineLocationDisplayName(location, t),
      })),
    [sortedMachineLocations, t]
  )
  const isEdit = hazard != null
  const [form, setForm] = useState(() =>
    hazard
      ? {
          riskInInjury: hazard.riskInInjury ?? '',
          riskInFrequency: hazard.riskInFrequency ?? '',
          riskInAvoidance: hazard.riskInAvoidance ?? '',
          riskInLikelihood: hazard.riskInLikelihood ?? '',
          riskInInjuryJustification: hazard.riskInInjuryJustification ?? '',
          riskInFrequencyJustification:
            hazard.riskInFrequencyJustification ?? '',
          riskInAvoidanceJustification:
            hazard.riskInAvoidanceJustification ?? '',
          riskInLikelihoodJustification:
            hazard.riskInLikelihoodJustification ?? '',
          machineLocationId: hazard.machineLocationId ?? '',
          type: hazard.type ?? '',
          lifecycles: hazard.lifecycles ?? [],
          description: hazard.description ?? '',
          pictogramId: hazard.pictogramId ?? null,
          ehsrClauseIds: hazard.ehsrClauseIds ?? [],
        }
      : initialForm
  )

  const isValid = useMemo(() => validateForm(form), [form])
  const canSubmit = isValid && !isCreating && !isUpdating
  const hazardTypeItems = useMemo(
    () =>
      HAZARD_TYPES.map((value) => ({
        value,
        label: t(`hazardsPage.enums.hazardTypes.${value}`),
      })),
    [t]
  )

  const handleClose = useCallback(() => {
    setForm(
      hazard
        ? {
            riskInInjury: hazard.riskInInjury ?? '',
            riskInFrequency: hazard.riskInFrequency ?? '',
            riskInAvoidance: hazard.riskInAvoidance ?? '',
            riskInLikelihood: hazard.riskInLikelihood ?? '',
            riskInInjuryJustification: hazard.riskInInjuryJustification ?? '',
            riskInFrequencyJustification:
              hazard.riskInFrequencyJustification ?? '',
            riskInAvoidanceJustification:
              hazard.riskInAvoidanceJustification ?? '',
            riskInLikelihoodJustification:
              hazard.riskInLikelihoodJustification ?? '',
            machineLocationId: hazard.machineLocationId ?? '',
            type: hazard.type ?? '',
            lifecycles: hazard.lifecycles ?? [],
            description: hazard.description ?? '',
            pictogramId: hazard.pictogramId ?? null,
            ehsrClauseIds: hazard.ehsrClauseIds ?? [],
          }
        : initialForm
    )
    onClose()
  }, [onClose, hazard])

  const handleSubmit = useCallback(() => {
    if (!canSubmit) {
      return
    }
    const payload = {
      riskInInjury: form.riskInInjury.trim(),
      riskInFrequency: form.riskInFrequency.trim(),
      riskInAvoidance: form.riskInAvoidance.trim(),
      riskInLikelihood: form.riskInLikelihood.trim(),
      riskInInjuryJustification: form.riskInInjuryJustification.trim() || null,
      riskInFrequencyJustification:
        form.riskInFrequencyJustification.trim() || null,
      riskInAvoidanceJustification:
        form.riskInAvoidanceJustification.trim() || null,
      riskInLikelihoodJustification:
        form.riskInLikelihoodJustification.trim() || null,
      machineLocationId: form.machineLocationId.trim(),
      type: form.type.trim(),
      lifecycles: form.lifecycles,
      description: form.description.trim(),
      pictogramId: form.pictogramId?.trim() || null,
      ehsrClauseIds: form.ehsrClauseIds.length > 0 ? form.ehsrClauseIds : null,
    }
    if (isEdit && hazard && onUpdate) {
      onUpdate({ hazardId: hazard.id, ...payload })
    } else {
      onCreate({
        ...payload,
        source: 'USER',
      })
      setForm(initialForm)
    }
  }, [canSubmit, form, isEdit, hazard, onCreate, onUpdate])

  const toggleLifecycle = useCallback((value: string) => {
    setForm((prev) => {
      const next = prev.lifecycles.includes(value)
        ? prev.lifecycles.filter((lifecycle) => lifecycle !== value)
        : [...prev.lifecycles, value]
      return { ...prev, lifecycles: next }
    })
  }, [])

  const isPending = isCreating || isUpdating

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className='sm:max-w-3xl'
        showCloseButton={!isPending}
        {...dialogPortaledPopoverGuard}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('hazardsPage.formDialog.editTitle')
              : t('hazardsPage.formDialog.addTitle')}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea type='always' className='-mx-6 max-h-[50vh] min-h-0 px-6'>
          <div className='space-y-4 pe-3'>
            <div className='space-y-2'>
              <Label htmlFor='add-hazard-type'>
                {t('hazardsPage.hazardDetail.type')}{' '}
                <span className='text-destructive'>*</span>
              </Label>
              <Select
                value={form.type || undefined}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, type: value ?? '' }))
                }
                items={hazardTypeItems}
              >
                <SelectTrigger id='add-hazard-type' className='w-full'>
                  <SelectValue
                    placeholder={t('hazardsPage.hazardDetail.typePlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {HAZARD_TYPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`hazardsPage.enums.hazardTypes.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='add-hazard-location'>
                {t('hazardsPage.hazardDetail.location')}{' '}
                <span className='text-destructive'>*</span>
              </Label>
              <Select
                value={form.machineLocationId || undefined}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    machineLocationId: value ?? '',
                  }))
                }
                items={machineLocationItems}
              >
                <SelectTrigger id='add-hazard-location' className='w-full'>
                  <SelectValue
                    placeholder={t(
                      'hazardsPage.hazardDetail.locationPlaceholder'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {sortedMachineLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {getMachineLocationDisplayName(location, t)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='add-hazard-description'>
                {t('hazardsPage.hazardDetail.description')}{' '}
                <span className='text-destructive'>*</span>
              </Label>
              <Textarea
                id='add-hazard-description'
                value={form.description}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
                placeholder={t(
                  'hazardsPage.hazardDetail.descriptionPlaceholder'
                )}
                rows={4}
              />
            </div>

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div className='space-y-3'>
                <div className='space-y-2'>
                  <Label htmlFor='add-hazard-risk-in-injury'>
                    {t('hazardsPage.hazardDetail.riskFlow.injury')}{' '}
                    <span className='text-destructive'>*</span>
                  </Label>
                  <RiskInjurySelect
                    id='add-hazard-risk-in-injury'
                    value={form.riskInInjury}
                    onValueChange={(value) => {
                      if (value) {
                        setForm((prev) => ({ ...prev, riskInInjury: value }))
                      }
                    }}
                    placeholder={t('hazardsPage.hazardDetail.riskFlow.select')}
                  />
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='add-hazard-risk-in-injury-justification'
                    className='text-xs text-muted-foreground'
                  >
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </Label>
                  <Textarea
                    id='add-hazard-risk-in-injury-justification'
                    value={form.riskInInjuryJustification}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        riskInInjuryJustification: event.target.value,
                      }))
                    }
                    rows={4}
                    placeholder={t(
                      'hazardsPage.formDialog.riskInJustificationPlaceholder'
                    )}
                    className='resize-none text-xs'
                  />
                </div>
              </div>

              <div className='space-y-3'>
                <div className='space-y-2'>
                  <Label htmlFor='add-hazard-risk-in-frequency'>
                    {t('hazardsPage.hazardDetail.riskFlow.frequency')}{' '}
                    <span className='text-destructive'>*</span>
                  </Label>
                  <RiskFrequencySelect
                    id='add-hazard-risk-in-frequency'
                    value={form.riskInFrequency}
                    onValueChange={(value) => {
                      if (value) {
                        setForm((prev) => ({ ...prev, riskInFrequency: value }))
                      }
                    }}
                    placeholder={t('hazardsPage.hazardDetail.riskFlow.select')}
                  />
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='add-hazard-risk-in-frequency-justification'
                    className='text-xs text-muted-foreground'
                  >
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </Label>
                  <Textarea
                    id='add-hazard-risk-in-frequency-justification'
                    value={form.riskInFrequencyJustification}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        riskInFrequencyJustification: event.target.value,
                      }))
                    }
                    rows={4}
                    placeholder={t(
                      'hazardsPage.formDialog.riskInJustificationPlaceholder'
                    )}
                    className='resize-none text-xs'
                  />
                </div>
              </div>

              <div className='space-y-3'>
                <div className='space-y-2'>
                  <Label htmlFor='add-hazard-risk-in-avoidance'>
                    {t('hazardsPage.hazardDetail.riskFlow.avoidance')}{' '}
                    <span className='text-destructive'>*</span>
                  </Label>
                  <RiskAvoidanceSelect
                    id='add-hazard-risk-in-avoidance'
                    value={form.riskInAvoidance}
                    onValueChange={(value) => {
                      if (value) {
                        setForm((prev) => ({ ...prev, riskInAvoidance: value }))
                      }
                    }}
                    placeholder={t('hazardsPage.hazardDetail.riskFlow.select')}
                  />
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='add-hazard-risk-in-avoidance-justification'
                    className='text-xs text-muted-foreground'
                  >
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </Label>
                  <Textarea
                    id='add-hazard-risk-in-avoidance-justification'
                    value={form.riskInAvoidanceJustification}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        riskInAvoidanceJustification: event.target.value,
                      }))
                    }
                    rows={4}
                    placeholder={t(
                      'hazardsPage.formDialog.riskInJustificationPlaceholder'
                    )}
                    className='resize-none text-xs'
                  />
                </div>
              </div>

              <div className='space-y-3'>
                <div className='space-y-2'>
                  <Label htmlFor='add-hazard-risk-in-likelihood'>
                    {t('hazardsPage.hazardDetail.riskFlow.likelihood')}{' '}
                    <span className='text-destructive'>*</span>
                  </Label>
                  <RiskLikelihoodSelect
                    id='add-hazard-risk-in-likelihood'
                    value={form.riskInLikelihood}
                    onValueChange={(value) => {
                      if (value) {
                        setForm((prev) => ({ ...prev, riskInLikelihood: value }))
                      }
                    }}
                    placeholder={t('hazardsPage.hazardDetail.riskFlow.select')}
                  />
                </div>
                <div className='space-y-2'>
                  <Label
                    htmlFor='add-hazard-risk-in-likelihood-justification'
                    className='text-xs text-muted-foreground'
                  >
                    {t(
                      'hazardsPage.hazardDetail.riskFlow.justificationOptional'
                    )}
                  </Label>
                  <Textarea
                    id='add-hazard-risk-in-likelihood-justification'
                    value={form.riskInLikelihoodJustification}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        riskInLikelihoodJustification: event.target.value,
                      }))
                    }
                    rows={4}
                    placeholder={t(
                      'hazardsPage.formDialog.riskInJustificationPlaceholder'
                    )}
                    className='resize-none text-xs'
                  />
                </div>
              </div>
            </div>

            <div className='space-y-2'>
              <Label>
                {t('hazardsPage.hazardDetail.lifecyclePhases')}{' '}
                <span className='text-destructive'>*</span>
              </Label>
              <p className='text-xs text-muted-foreground'>
                {t('hazardsPage.hazardDetail.lifecyclePhasesHint')}
              </p>
              <div className='flex flex-wrap gap-4 rounded-md border bg-muted/30 p-3'>
                {HAZARD_LIFECYCLES.map((value) => (
                  <div key={value} className='flex items-center gap-2'>
                    <Checkbox
                      id={`lifecycle-${value}`}
                      checked={form.lifecycles.includes(value)}
                      onCheckedChange={() => toggleLifecycle(value)}
                    />
                    <Label
                      htmlFor={`lifecycle-${value}`}
                      className='cursor-pointer text-sm font-normal'
                    >
                      {t(`hazardsPage.enums.hazardLifecycles.${value}`)}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='add-hazard-pictogram'>
                {t('hazardsPage.hazardDetail.pictogram')}
              </Label>
              <PictogramSelect
                id='add-hazard-pictogram'
                value={form.pictogramId}
                onChange={(pictogramId) =>
                  setForm((prev) => ({ ...prev, pictogramId }))
                }
                placeholder={t('hazardsPage.hazardDetail.pictogramNone')}
              />
            </div>

            <div className='space-y-2'>
              <Label>{t('hazardsPage.hazardDetail.ehsr')}</Label>
              <EhsrSelect
                options={ehsrOptions}
                value={form.ehsrClauseIds}
                onChange={(ehsrClauseIds) =>
                  setForm((prev) => ({ ...prev, ehsrClauseIds }))
                }
                placeholder={t('hazardsPage.hazardDetail.ehsrPlaceholder')}
              />
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className='border-t pt-4 sm:justify-end'>
          <Button variant='outline' onClick={handleClose} disabled={isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!canSubmit} onClick={handleSubmit}>
            {isEdit
              ? t('hazardsPage.formDialog.saveButton')
              : t('common:actions.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
